import { lazy, Suspense, useState, useCallback, useEffect } from 'react';
import { LEGACY_PAGES } from './modules/dashboard-demo/legacy/registry.js';
import LegacyPage from './modules/dashboard-demo/legacy/LegacyPage.jsx';
import { AuthProvider, useAuth } from './shared/auth/AuthContext.jsx';
import OrbitAIProvider from './shared/orbit-ai/OrbitAIProvider.jsx';
import { WorkspaceProvider } from './shared/workspace/WorkspaceContext.jsx';
import LoginPage from './shared/auth/LoginPage.jsx';
import OrbitModuleHeader from './shared/hub/OrbitModuleHeader.jsx';
import HubPage from './shared/hub/HubPage.jsx';
import UsersPage from './modules/user-management/UsersPage.jsx';
import ClientManagementApp from './modules/client-management/ClientManagementApp.jsx';
import OrbitDevelopmentApp from './modules/orbit-development/OrbitDevelopmentApp.jsx';
import SqlEditorPage from './modules/sql-query-tool/SqlEditorPage.jsx';
const ProjectManagementApp = lazy(
  () => import('./modules/project-management/ProjectManagementApp.jsx'));
import TaxonomyApp from './modules/taxonomy-generator/TaxonomyApp.jsx';
import ReachToolApp from './modules/reach-tool/ReachToolApp.jsx';
import PaidMediaToolApp from './modules/paid-media/PaidMediaToolApp.jsx';
import CompetitiveMonitoringApp from './modules/competitive-monitoring/CompetitiveMonitoringApp.jsx';
import { MODULES, moduleForPath } from './shared/modules/registry.js';

const SettingsApp = lazy(() => import('./modules/settings/SettingsApp.jsx'));

const MODULE = 'dashboard-demo';

const DASHBOARD_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'paid-media', label: 'Campaign' },
  { id: 'crm', label: 'CRM' },
  { id: 'wa-acquisition', label: 'Website Analytics' },
  { id: 'social-organic', label: 'Social Organic' },
  { id: 'social-listening', label: 'Social Listening' },
];

// ─── routing helpers ──────────────────────────────────────────────────────────

function getPath() {
  return window.location.pathname;
}

function pageIdFromPath(path) {
  // /dashboard-demo/<id>  →  id
  const prefix = '/' + MODULE + '/';
  if (!path.startsWith(prefix)) return null;
  const id = path.slice(prefix.length);
  return LEGACY_PAGES.some(p => p.id === id) ? id : null;
}

// ─── inner router ────────────────────────────────────────────────────────────

function Router() {
  const { user, loading } = useAuth();
  const [path, setPath] = useState(getPath);
  // A local-only visual review surface for the Paid Media module. `import.meta.env.DEV`
  // is false in production builds, so this never creates an unauthenticated product route.
  const paidMediaPreview = import.meta.env.DEV && path === '/paid-media-preview';

  useEffect(() => {
    const onPop = () => setPath(getPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // The browser tab names where you are: the module you are inside, or Orbit on the Hub and
  // on login. Set here rather than in each module because routing is hand-rolled — there is no
  // router to hang it off — and because the registry already answers "which module owns this
  // path" for the Hub and the backend. A module that renders its own header therefore needs
  // no title code of its own, and one that doesn't still gets a correct tab.
  useEffect(() => {
    // moduleForPath matches a module's registered entry path, which is exact for every module
    // but one: Dashboard Demo registers /dashboard-demo/overview, so its other legacy pages
    // would fall through to "Orbit" while sitting squarely inside the tool. Falling back to the
    // first path segment covers that without naming it — and still leaves /login and the Hub,
    // which match no module, as Orbit.
    const seg = p => '/' + ((p || '').split('/')[1] || '');
    const mod = moduleForPath(path)
      || MODULES.find(m => m.path && seg(m.path) === seg(path));
    document.title = mod ? mod.label : 'Orbit';
  }, [path]);

  const navigateDashboard = useCallback((id) => {
    history.pushState(null, '', '/' + MODULE + '/' + id);
    window.scrollTo(0, 0);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  // Intercept data-nav clicks inside legacy pages → /dashboard-demo/<id>
  const onClickCapture = useCallback((e) => {
    const link = e.target.closest('[data-nav]');
    if (!link) return;
    const id = link.getAttribute('data-nav');
    if (LEGACY_PAGES.some(p => p.id === id)) {
      e.preventDefault();
      navigateDashboard(id);
    }
  }, [navigateDashboard]);

  if (loading) return <Spinner />;

  if (paidMediaPreview) {
    return <PaidMediaToolApp preview />;
  }

  const AUTH_ENABLED = true;

  // ── Public route: login ───────────────────────────────────────────────────
  if (path === '/orbit-ai' && user) return <HubPage />;

  if (path === '/login') {
    if (!AUTH_ENABLED || user) {
      history.replaceState(null, '', '/');
      setPath('/');
      return null;
    }
    return <LoginPage />;
  }

  // ── Auth guard ────────────────────────────────────────────────────────────
  if (AUTH_ENABLED && !user) {
    const dest = `/login${path !== '/' ? '?redirect=' + encodeURIComponent(path) : ''}`;
    history.replaceState(null, '', dest);
    setPath('/login');
    return <LoginPage />;
  }

  // ── Hub ───────────────────────────────────────────────────────────────────
  if (path === '/' || path === '') {
    return <HubPage />;
  }

  // ── Users management ──────────────────────────────────────────────────────
  if (path === '/settings' || path.startsWith('/settings/')) {
    return <Suspense fallback={<Spinner />}><SettingsApp /></Suspense>;
  }

  if (path === '/users') {
    return <UsersPage />;
  }

  // ── Client Management ─────────────────────────────────────────────────────
  if (path.startsWith('/client-management')) {
    return <ClientManagementApp />;
  }

  // ── Orbit Development ─────────────────────────────────────────────────────
  if (path.startsWith('/orbit-development')) {
    return <OrbitDevelopmentApp />;
  }

  // ── SQL Editor ────────────────────────────────────────────────────────────
  if (path === '/sql-editor' || path.startsWith('/sql-editor/')) {
    return <SqlEditorPage />;
  }

  // ── Project Management ────────────────────────────────────────────────────
  if (path.startsWith('/project-management')) {
    return <Suspense fallback={<Spinner />}><ProjectManagementApp /></Suspense>;
  }

  // ── Taxonomy Generator ────────────────────────────────────────────────────
  if (path.startsWith('/taxonomy-generator')) {
    return <TaxonomyApp />;
  }

  // ── Reach Tool ─────────────────────────────────────────────────────────────
  if (path.startsWith('/reach-tool')) {
    return <ReachToolApp />;
  }

  // ── Paid Media Tool ──────────────────────────────────────────────────────
  if (path.startsWith('/paid-media')) {
    return <PaidMediaToolApp />;
  }

  // ── Competitive Monitoring ─────────────────────────────────────────────────
  if (path.startsWith('/competitive-monitoring')) {
    return <CompetitiveMonitoringApp />;
  }

  // ── Dashboard-demo pages ──────────────────────────────────────────────────
  const pageId = pageIdFromPath(path);
  if (pageId) {
    const page = LEGACY_PAGES.find(p => p.id === pageId);
    return (
      <div className="o-legacy-shell" onClickCapture={onClickCapture}>
        <OrbitModuleHeader
          title="Dashboard Demo"
          tabs={DASHBOARD_TABS}
          activeTab={pageId.startsWith('wa-') ? 'wa-acquisition' : pageId}
          onTabChange={navigateDashboard}
        />
        <LegacyPage key={page.id} page={page} />
      </div>
    );
  }

  // ── Unknown path → hub ────────────────────────────────────────────────────
  history.replaceState(null, '', '/');
  setPath('/');
  return <HubPage />;
}

function Spinner() {
  return (
    <div className="o-state" style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      Loading…
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      {/* Inside AuthProvider because it needs the token; outside Router so the selected
          workspace survives navigation. */}
      <WorkspaceProvider>
        <OrbitAIProvider><Router /></OrbitAIProvider>
      </WorkspaceProvider>
    </AuthProvider>
  );
}
